export type QualityPreset = 'low' | 'medium' | 'high' | 'ultra';

export interface GraphicsSettings {
  preset: QualityPreset;
  shadows: 0 | 1 | 2 | 3; // off, low, medium, high
  textureRes: 256 | 512;
  terrainDetail: 1 | 2;
  vegetation: number; // 0..1 grass density
  water: 0 | 1; // simple/full
  particles: number; // 0..1
  antialias: boolean;
  renderScale: number; // 0.5..1 of device pixel ratio cap
  maxPixelRatio: number;
  timeOfDay: 'dawn' | 'day' | 'dusk';
  /** Lower the render resolution automatically to hold the frame rate. */
  dynamicResolution?: boolean;
  /** Soft contact shadows under units and buildings (one extra instanced draw, no extra pass). */
  contactShadows: boolean;
  /** Glow around fires and flames (additive sprites, no bloom pass). */
  glow: boolean;
}

export const PRESETS: Record<QualityPreset, Omit<GraphicsSettings, 'preset' | 'timeOfDay'>> = {
  low: { shadows: 0, textureRes: 256, terrainDetail: 1, vegetation: 0, water: 0, particles: 0.4, antialias: false, renderScale: 0.75, maxPixelRatio: 1, contactShadows: false, glow: false },
  // Medium uses the GPU's own multisampling: on tile-based GPUs (Apple M1, phones) it is resolved
  // on chip at little cost, and it needs no extra pass, unlike SMAA or FXAA.
  medium: { shadows: 1, textureRes: 256, terrainDetail: 1, vegetation: 0.4, water: 1, particles: 0.7, antialias: true, renderScale: 1, maxPixelRatio: 1.25, contactShadows: true, glow: true },
  high: { shadows: 2, textureRes: 512, terrainDetail: 2, vegetation: 0.8, water: 1, particles: 1, antialias: true, renderScale: 1, maxPixelRatio: 1.5, contactShadows: true, glow: true },
  ultra: { shadows: 3, textureRes: 512, terrainDetail: 2, vegetation: 1, water: 1, particles: 1, antialias: true, renderScale: 1, maxPixelRatio: 2, contactShadows: true, glow: true },
};

export function settingsFromPreset(p: QualityPreset): GraphicsSettings {
  return { preset: p, timeOfDay: 'day', ...PRESETS[p] };
}

/** Pick a default preset from device characteristics. */
export function detectPreset(): QualityPreset {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
  const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency ?? 4 : 4;
  if (cores >= 8 && dpr >= 2) return 'high';
  if (cores >= 4) return 'medium';
  return 'low';
}
